import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Share2, Heart, Info, MessageSquare, Map, List, Bell, BookOpen, Phone, MapPin } from 'lucide-react';
import { navigationClient } from '../lib/navigation';
import { useAirtableQuery } from '../hooks/useAirtableQuery';
import { useDevice } from '../hooks/useDevice';
import type { SupportedLanguages } from '../i18n';
import { SVGIcon } from './shared/SVGIcon';
import { FirstVisitBanner } from './pwa/FirstVisitBanner';
import { ShareModal } from './shared/ShareModal';
import { HotelModal } from './HotelModal';
import { analyticsService } from '../lib/analytics/analyticsService';
import { favoritesService } from '../lib/favorites/favoritesService';
import { resolveColor } from '../utils/colorUtils';
import { SocialNetworksModal } from './socialnetworks/SocialNetworksModal';

interface NavigationButtonsProps {
  onLivechatClick: (url: string) => void;
  onSocialNetworksClick: () => void;
  messageCount: number;
}

export const NavigationButtons: React.FC<NavigationButtonsProps> = ({
  onLivechatClick,
  onSocialNetworksClick,
  messageCount
}) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [showInstallPanel, setShowInstallPanel] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showHotelModal, setShowHotelModal] = useState(false);
  const { os, isDesktop } = useDevice();

  const isPWA = window.matchMedia('(display-mode: standalone)').matches;

  const { data: config } = useAirtableQuery.useConfig();
  const { data: translations } = useAirtableQuery.useTranslations(i18n.language as SupportedLanguages);
  const { data: navigation } = useQuery({
    queryKey: ['navigation'],
    queryFn: () => navigationClient.getNavigation(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });

  if (!config || !translations || !navigation) {
    return null;
  }

  const favoritesCount = favoritesService.getFavorites().length;
  const iconColor = resolveColor(config.nav_icon, config, config['light-color']);

  const favoritesButton = {
    id: 'favorites',
    type_rubrique: 'link' as const,
    names: {
      fr: 'Favoris', en: 'Favorites', es: 'Favoritos', it: 'Preferiti', pt: 'Favoritos',
      de: 'Favoriten', nl: 'Favorieten', ja: 'お気に入り', zh: '收藏夹', ru: 'Избранное'
    },
    icon: (
      <div className="relative">
        <Heart className="w-14 h-14" fill={iconColor} stroke={iconColor} />
        {favoritesCount > 0 && (
          <div className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full text-xs font-medium"
            style={{ 
              backgroundColor: resolveColor(config.count_back, config, config['major-color']),
              color: resolveColor(config.count_txt, config, config['light-color'])
            }}>
            {favoritesCount > 99 ? '99+' : favoritesCount}
          </div>
        )}
      </div>
    ),
    onClick: () => {
      analyticsService.trackEvent({
        event_type: 'navigation',
        event_name: 'menu_click',
        event_data: {
          section_id: 'favorites',
          section_name: 'Favorites',
          section_type: 'favorites'
        }
      });
      navigate('/favoris');
    }
  };

  const shareButton = {
    id: 'share',
    type_rubrique: 'share' as const,
    names: {
      fr: 'Partager', en: 'Share', es: 'Compartir', it: 'Condividi', pt: 'Compartilhar',
      de: 'Teilen', nl: 'Delen', ja: 'シェア', zh: '分享', ru: 'Поделиться'
    },
    icon: (
      <Share2 className="w-14 h-14" stroke={iconColor} fill="none" strokeWidth={1.5} />
    ),
    onClick: () => {
      analyticsService.trackEvent({
        event_type: 'interaction',
        event_name: 'share_modal_open',
        event_data: {
          section_id: 'share',
          section_name: 'Share',
          section_type: 'share'
        }
      });
      setShowShareModal(true);
    }
  };

  const filteredNavigation = navigation.filter(item => {
    if (item.type_rubrique === 'webapp') {
      return !isDesktop && !isPWA;
    }
    return true;
  });

  const navigationWithExtras = [favoritesButton, ...filteredNavigation, shareButton];

  const handleNavigation = (item: any) => {
    analyticsService.trackEvent({
      event_type: 'navigation',
      event_name: 'menu_click',
      event_data: {
        section_id: item.id,
        section_name: item.names.fr,
        section_type: item.type_rubrique
      }
    });

    if (item.onClick) {
      item.onClick();
      return;
    }

    switch (item.type_rubrique) {
      case 'hotel-info':
        analyticsService.trackEvent({
          event_type: 'interaction',
          event_name: 'hotel_info_open',
          event_data: { section_id: item.id }
        });
        setShowHotelModal(true);
        break;
      case 'livechat':
        if (item.urlLivechat) {
          analyticsService.trackEvent({
            event_type: 'interaction',
            event_name: 'livechat_open',
            event_data: {
              section_id: item.id,
              section_name: item.names.fr,
              section_type: 'livechat'
            }
          });
          onLivechatClick(item.urlLivechat);
        }
        break;
      case 'socialnetworks':
        analyticsService.trackEvent({
          event_type: 'interaction',
          event_name: 'social_networks_modal_open',
          event_data: {
            section_id: item.id,
            section_name: item.names.fr,
            section_type: 'socialnetworks'
          }
        });
        onSocialNetworksClick();
        break;
      case 'webapp':
        analyticsService.trackEvent({
          event_type: 'interaction',
          event_name: 'webapp_install_prompt',
          event_data: {
            section_id: item.id,
            section_name: item.names.fr,
            section_type: 'webapp'
          }
        });
        setShowInstallPanel(true);
        break;
      case 'link':
        if (item.fulladress) {
          analyticsService.trackEvent({
            event_type: 'navigation',
            event_name: 'external_link',
            event_data: {
              section_id: item.id,
              section_type: 'link',
              section_name: item.names.fr,
              link_type: 'maps',
              url: item.fulladress
            }
          });
          const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(item.fulladress)}`;
          window.open(url, '_blank', 'noopener,noreferrer');
        } else if (item.url) {
          analyticsService.trackEvent({
            event_type: 'navigation',
            event_name: 'external_link',
            event_data: {
              section_id: item.id,
              section_type: 'link',
              section_name: item.names.fr,
              link_type: 'url',
              url: item.url
            }
          });
          const url = item.url.startsWith('http') ? item.url : `https://${item.url}`;
          window.open(url, '_blank', 'noopener,noreferrer');
        }
        break;
      case 'itineraire':
        if (item.fulladress) {
          analyticsService.trackEvent({
            event_type: 'navigation',
            event_name: 'external_link',
            event_data: {
              section_id: item.id,
              section_type: 'itineraire',
              section_name: item.names.fr,
              link_type: 'maps',
              url: item.fulladress
            }
          });
          const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(item.fulladress)}`;
          window.open(url, '_blank', 'noopener,noreferrer');
        }
        break;
      default:
        const slug = navigationClient.generateSlug(item.names.fr);
        const path = `/${slug}`;

        analyticsService.trackEvent({
          event_type: 'navigation',
          event_name: 'internal_navigation',
          event_data: {
            section_id: item.id,
            section_name: item.names.fr,
            section_type: item.type_rubrique,
            path: path
          }
        });

        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;

        navigate(path);
    }
  };

  const getDefaultIcon = (type: string) => {
    switch (type) {
      case 'hotel-info': return <Info className="w-14 h-14" stroke={iconColor} fill="none" />;
      case 'livechat': return <MessageSquare className="w-14 h-14" stroke={iconColor} fill="none" />;
      case 'standard': return <List className="w-14 h-14" stroke={iconColor} fill="none" />;
      case 'event':
      case 'event-api': return <Bell className="w-14 h-14" stroke={iconColor} fill="none" />;
      case 'liste': return <BookOpen className="w-14 h-14" stroke={iconColor} fill="none" />;
      case 'link': return <Phone className="w-14 h-14" stroke={iconColor} fill="none" />;
      case 'itineraire': return <MapPin className="w-14 h-14" stroke={iconColor} fill="none" />;
      case 'socialnetworks': return <Share2 className="w-14 h-14" stroke={iconColor} fill="none" />;
      case 'webapp': return <Map className="w-14 h-14" stroke={iconColor} fill="none" />;
      default: return <Info className="w-14 h-14" stroke={iconColor} fill="none" />;
    }
  };

  return (
    <div className="flex-1 flex items-center justify-center py-8">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 w-fit mx-auto px-4">
        {navigationWithExtras.map((item) => {
          const name = item.names[i18n.language as SupportedLanguages] || item.names.fr;

          return (
            <div key={item.id} className="w-full max-w-[240px]">
              <button
                onClick={() => handleNavigation(item)}
                className="relative flex flex-col items-center justify-center p-6 shadow-md hover:shadow-lg transition-all duration-200 aspect-square w-full group overflow-hidden"
                style={{ 
                  borderRadius: config.border_radius,
                  background: `${config['major-color']}BF`,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)'
                }}
              >
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  style={{
                    background: 'linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3))'
                  }}
                />
                {React.isValidElement(item.icon) ? (
                  item.icon
                ) : item.icon ? (
                  <SVGIcon 
                    url={item.icon} 
                    color={resolveColor(config.nav_icon, config, config['light-color'])}
                    className="w-14 h-14 mb-3 relative"
                  />
                ) : (
                  <div className="mb-3">
                    {getDefaultIcon(item.type_rubrique)}
                  </div>
                )}
                <span 
                  className="text-center font-medium relative mt-3"
                  style={{ color: config['light-color'] }}
                >
                  {name}
                </span>
              </button>
            </div>
          );
        })}
      </div>

      {showInstallPanel && !isPWA && (
        <FirstVisitBanner
          config={config}
          onClose={() => setShowInstallPanel(false)}
          forceShow={true}
          platform={os === 'iOS' ? 'ios' : 'android'}
        />
      )}

      {showShareModal && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          config={config}
          translations={translations}
        />
      )}

      {showHotelModal && (
        <HotelModal
          config={config}
          translations={translations}
          onClose={() => setShowHotelModal(false)}
        />
      )}
    </div>
  );
};
